import React, { useState, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Quote, RefreshCw, Share2, Download, Twitter, 
  Facebook, Instagram, X, Check 
} from 'lucide-react';
import { getDailyQuote, getRandomQuote } from '../data/motivationalData';
import { toast } from 'sonner';

const DailyQuote = ({ showRefresh = false }) => {
  const [quote, setQuote] = useState(getDailyQuote());
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const canvasRef = useRef(null);

  const refreshQuote = () => {
    setQuote(getRandomQuote());
  };

  const generateShareImage = async () => {
    setIsGenerating(true);
    
    // Create canvas
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Set dimensions (Instagram story size: 1080x1920, we'll use 1080x1080 for square)
    canvas.width = 1080;
    canvas.height = 1080;
    
    // Background gradient
    const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
    gradient.addColorStop(0, '#1a1a2e');  // Dark blue
    gradient.addColorStop(0.5, '#16213e'); // Darker
    gradient.addColorStop(1, '#0f0f23');  // Almost black
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Add subtle grid pattern
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.03)';
    ctx.lineWidth = 1;
    for (let i = 0; i < canvas.width; i += 40) {
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i, canvas.height);
      ctx.stroke();
    }
    for (let i = 0; i < canvas.height; i += 40) {
      ctx.beginPath();
      ctx.moveTo(0, i);
      ctx.lineTo(canvas.width, i);
      ctx.stroke();
    }
    
    // Decorative accent line at top
    const accentGradient = ctx.createLinearGradient(0, 0, canvas.width, 0);
    accentGradient.addColorStop(0, '#f59e0b');  // Amber
    accentGradient.addColorStop(0.5, '#ef4444'); // Red
    accentGradient.addColorStop(1, '#f59e0b');  // Amber
    ctx.fillStyle = accentGradient;
    ctx.fillRect(0, 0, canvas.width, 6);
    
    // Quote marks decoration
    ctx.font = 'bold 200px Georgia';
    ctx.fillStyle = 'rgba(245, 158, 11, 0.1)';  // Amber with low opacity
    ctx.textAlign = 'left';
    ctx.fillText('"', 60, 280);
    ctx.textAlign = 'right';
    ctx.fillText('"', canvas.width - 60, canvas.height - 180);
    
    // Main quote text
    ctx.fillStyle = '#ffffff';
    ctx.font = 'italic 600 48px system-ui, -apple-system, sans-serif';
    ctx.textAlign = 'center';
    
    // Word wrap the quote
    const maxWidth = canvas.width - 160;
    const words = quote.quote.split(' ');
    let line = '';
    let lines = [];
    
    for (let word of words) {
      const testLine = line + word + ' ';
      const metrics = ctx.measureText(testLine);
      if (metrics.width > maxWidth && line !== '') {
        lines.push(line.trim());
        line = word + ' ';
      } else {
        line = testLine;
      }
    }
    lines.push(line.trim());
    
    // Center the quote vertically
    const lineHeight = 70;
    const totalTextHeight = lines.length * lineHeight;
    let startY = (canvas.height - totalTextHeight) / 2;
    
    // Draw each line
    lines.forEach((line, index) => {
      ctx.fillText(`"${index === 0 ? '' : ''}${line}${index === lines.length - 1 ? '"' : ''}`, canvas.width / 2, startY + (index * lineHeight));
    });
    
    // Author
    ctx.font = '500 32px system-ui, -apple-system, sans-serif';
    ctx.fillStyle = '#f59e0b';  // Amber
    ctx.fillText(`— ${quote.author}`, canvas.width / 2, startY + (lines.length * lineHeight) + 50);
    
    // Sport
    ctx.font = '400 24px system-ui, -apple-system, sans-serif';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
    ctx.fillText(quote.sport, canvas.width / 2, startY + (lines.length * lineHeight) + 90);
    
    // AURA Branding
    // Logo area
    ctx.fillStyle = '#f59e0b';
    ctx.font = 'bold 56px system-ui, -apple-system, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('AURA', canvas.width / 2, canvas.height - 120);
    
    // Tagline
    ctx.font = '400 20px system-ui, -apple-system, sans-serif';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
    ctx.fillText('Earned, Not Given', canvas.width / 2, canvas.height - 80);
    
    // Website
    ctx.font = '400 18px system-ui, -apple-system, sans-serif';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
    ctx.fillText('aura-athlete.com', canvas.width / 2, canvas.height - 50);
    
    setIsGenerating(false);
    return canvas;
  };

  const downloadImage = async () => {
    try {
      const canvas = await generateShareImage();
      const link = document.createElement('a');
      link.download = `aura-quote-${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('Quote image downloaded!');
      setShowShareMenu(false);
    } catch (error) {
      toast.error('Failed to generate image');
    }
  };

  const shareToTwitter = async () => {
    const text = `"${quote.quote}" — ${quote.author}\n\n#AuraApp #Motivation #SportsQuotes`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank', 'width=600,height=400');
    setShowShareMenu(false);
    toast.success('Opening Twitter...');
  };

  const shareToFacebook = async () => {
    const url = `https://www.facebook.com/sharer/sharer.php?quote=${encodeURIComponent(`"${quote.quote}" — ${quote.author}`)}`;
    window.open(url, '_blank', 'width=600,height=400');
    setShowShareMenu(false);
    toast.success('Opening Facebook...');
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(`"${quote.quote}" — ${quote.author}, ${quote.sport}\n\n#AuraApp`);
      toast.success('Quote copied to clipboard!');
      setShowShareMenu(false);
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const shareNative = async () => {
    if (navigator.share) {
      try {
        const canvas = await generateShareImage();
        canvas.toBlob(async (blob) => {
          const file = new File([blob], 'aura-quote.png', { type: 'image/png' });
          await navigator.share({
            title: 'AURA Daily Quote',
            text: `"${quote.quote}" — ${quote.author}`,
            files: [file]
          });
          setShowShareMenu(false);
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          // Fallback to download
          downloadImage();
        }
      }
    } else {
      // Fallback for browsers without native share
      downloadImage();
    }
  };

  return (
    <Card className="bg-gradient-to-br from-amber-500/10 to-orange-500/5 border-amber-500/30 relative">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
            <Quote className="w-5 h-5 text-amber-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-amber-400 uppercase tracking-wider mb-1 flex items-center gap-2">
              Daily Inspiration
              {showRefresh && (
                <button 
                  onClick={refreshQuote}
                  className="p-1 hover:bg-white/10 rounded transition-colors"
                  data-testid="refresh-quote-btn"
                >
                  <RefreshCw className="w-3 h-3" />
                </button>
              )}
            </p>
            <p className="text-white italic">"{quote.quote}"</p>
            <p className="text-sm text-aura-text-muted mt-2">
              — {quote.author}, {quote.sport}
            </p>
          </div>
          
          {/* Share Button */}
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowShareMenu(!showShareMenu)}
              className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
              data-testid="share-quote-btn"
            >
              <Share2 className="w-4 h-4" />
            </Button>
            
            {/* Share Menu Dropdown */}
            {showShareMenu && (
              <div className="absolute right-0 top-full mt-2 w-56 bg-aura-surface border border-white/20 rounded-lg shadow-xl z-50 overflow-hidden">
                <div className="p-2 border-b border-white/10">
                  <p className="text-xs text-aura-text-muted uppercase tracking-wider px-2">
                    Share Quote
                  </p>
                </div>
                
                <div className="p-1">
                  <button
                    onClick={downloadImage}
                    disabled={isGenerating}
                    className="w-full flex items-center gap-3 px-3 py-2 text-sm text-white hover:bg-white/10 rounded-md transition-colors"
                    data-testid="download-quote-btn"
                  >
                    <Download className="w-4 h-4 text-green-400" />
                    Download Image
                  </button>
                  
                  <button
                    onClick={shareToTwitter}
                    className="w-full flex items-center gap-3 px-3 py-2 text-sm text-white hover:bg-white/10 rounded-md transition-colors"
                    data-testid="share-twitter-btn"
                  >
                    <Twitter className="w-4 h-4 text-blue-400" />
                    Share on Twitter/X
                  </button>
                  
                  <button
                    onClick={shareToFacebook}
                    className="w-full flex items-center gap-3 px-3 py-2 text-sm text-white hover:bg-white/10 rounded-md transition-colors"
                    data-testid="share-facebook-btn"
                  >
                    <Facebook className="w-4 h-4 text-blue-500" />
                    Share on Facebook
                  </button>
                  
                  <button
                    onClick={copyToClipboard}
                    className="w-full flex items-center gap-3 px-3 py-2 text-sm text-white hover:bg-white/10 rounded-md transition-colors"
                    data-testid="copy-quote-btn"
                  >
                    <Check className="w-4 h-4 text-purple-400" />
                    Copy to Clipboard
                  </button>
                  
                  {navigator.share && (
                    <button
                      onClick={shareNative}
                      className="w-full flex items-center gap-3 px-3 py-2 text-sm text-white hover:bg-white/10 rounded-md transition-colors"
                      data-testid="share-native-btn"
                    >
                      <Share2 className="w-4 h-4 text-amber-400" />
                      More Options...
                    </button>
                  )}
                </div>
                
                {/* Close button */}
                <div className="p-1 border-t border-white/10">
                  <button
                    onClick={() => setShowShareMenu(false)}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs text-aura-text-muted hover:bg-white/10 rounded-md transition-colors"
                  >
                    <X className="w-3 h-3" />
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
      
      {/* Hidden canvas for image generation */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </Card>
  );
};

export default DailyQuote;
